"use client"

import { Dispatch, SetStateAction, useContext, useEffect, useRef, useState } from "react";
import Image from "next/image"
import Link from "next/link";

import { Button, Divider } from "@heroui/react";
import { motion, useInView } from "framer-motion";
import { DateTime } from 'luxon'

import { CountdownTimer } from "@/components/countdownTimer";
import { CountdownContext } from "@/lib/CountdownContext";

type TypewriterProps = {
    text: string;
    speed?: number;
    className?: string;
};

const logoBanner = "/banner_logo.svg"
const AUDITION_CLOSE_DATE_TIME = "2026-04-13T23:59:59"
const TIMEZONE = "America/New_York"
const DATE_TIME = DateTime.fromISO(AUDITION_CLOSE_DATE_TIME, { zone: TIMEZONE });

export default function Intro({ setInView }: { setInView: Dispatch<SetStateAction<boolean | null>> }) {
    const [userDateString, setUserDateString] = useState<string | null>(null);
    const ref = useRef(null)
    const inView = useInView(ref, { initial: true })
    const { hasFinished } = useContext(CountdownContext);

    useEffect(() => {
        const USER_LOCALE_DATE_TIME = DATE_TIME.setZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
        setUserDateString(USER_LOCALE_DATE_TIME.toLocaleString(DateTime.DATETIME_FULL))
    }, [])

    useEffect(() => {
        setInView(inView)
    }, [inView, setInView])

    return (
        <>
            <div className="min-h-[900px] h-screen flex flex-col items-center justify-center md:pt-52 pt-96 z-40">
                <Typewriter className="second-header" text="Hey&nbsp;Gamers&nbsp;:)" speed={20} />
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <div ref={ref} className="flex flex-col lg:flex-row justify-center w-full items-center gap-2 lg:gap-x-8 pt-4">
                        <p className="font-bold text-xl lg:text-2xl text-primary-100">
                            We are
                        </p>
                    </div>

                    {/* for mobile animation */}
                    <div className="flex justify-center md:hidden">
                        <motion.div
                            className="flex justify-center w-[200px] h-auto"
                            layoutId="logo"
                            animate={{ y: inView ? 0 : -20 }}
                            transition={{ type: "tween", duration: 0.3, ease: "easeInOut" }}
                        >
                            <Image src="/banner_logo.svg" alt="NextGG" width={200} height={100} priority />
                        </motion.div>
                    </div>

                    <div className="md:flex justify-center hidden">
                        <Image src={logoBanner} alt="NextGG" width={300} height={150} priority />
                    </div>

                    <div className="flex flex-col md:flex-row justify-center w-full items-center gap-2 lg:gap-4 third-header">
                        <h3 className="text-xl sm:text-2xl text-primary-100">100% Self-Funded.</h3>
                        <h3 className="text-xl sm:text-2xl text-primary-100">Fully independent.</h3>
                    </div>

                    <div className="flex justify-center text-nowrap">
                        <h3 className="text-xl sm:text-2xl font-bold text-red-400 inline-block [text-shadow:0_2px_6px_rgba(0,0,0,1)]">🍁 Canadian Vtuber Agency 🍁</h3>
                    </div>

                    <div className="flex flex-col px-6 gap-4">
                        {hasFinished ? (
                            <>
                                <p className="second-header font-bold pt-14">AUDITIONS HAVE CLOSED</p>
                                <p className="third-header text-center">Thank you for applying! You will hear from us soon!</p>
                            </>
                        ) : (
                            <>
                                <p className="second-header font-bold pt-14">AUDITIONS ARE OPEN!</p>
                                <p className="flex justify-center text-white text-lg text-center text-pretty">Deadline: {userDateString}</p>
                            </>
                        )}
                    </div>
                    {!hasFinished && (<CountdownTimer targetDate={DATE_TIME.toJSDate()} />)}
                    <div className="flex flex-col justify-center items-center gap-4 py-5 text-center md:flex-row">
                        {!hasFinished && (
                            <Button
                                className="bg-primary-800 hover:outline-2 hover:outline-white outline-offset-2 transition-all ease-in-out text-white"
                                as={Link}
                                href="https://docs.google.com/forms/d/e/1FAIpQLSe0HY54xtPhDt3RNkATwmd5WwdChvYwts5wqkszUKfKYnHbVA/viewform"
                                target="_blank"
                                disableRipple
                                size="lg"
                                color="primary"
                            >
                                Apply Now!
                            </Button>
                        )}
                        <Button
                            className="bg-slate-800 hover:outline-2 hover:outline-white outline-offset-2 transition-all ease-in-out text-white"
                            disableRipple
                            size="lg"
                            as={Link}
                            href="#audition"
                        >
                            Learn More
                        </Button>
                    </div>
                    <Divider className="bg-slate-500 w-full hidden lg:block mt-24" orientation="horizontal" />
                    <div className="flex flex-col lg:flex-row flex-wrap justify-center items-center py-4 gap-6 lg:gap-8 xl:gap-16 text-center pt-12 lg:pt-4 px-10">
                        <p className="third-header">Sustainable Growth</p>
                        <Divider className="bg-slate-500 h-6" orientation="vertical" />
                        <p className="third-header">Talent Oriented</p>
                        <Divider className="bg-slate-500 h-6" orientation="vertical" />
                        <p className="third-header">Community Driven</p>
                    </div>
                </motion.div>
            </div>
        </>
    )
}

export function Typewriter({ text, speed = 80, className = "" }: TypewriterProps) {
    const letters = text.split("");

    const container = {
        hidden: { opacity: 1 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: speed / 1000,
            },
        },
    };

    const child = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0 },
    };

    return (
        <motion.span
            className={className}
            variants={container}
            initial="hidden"
            animate="visible"
        >
            {letters.map((letter, index) => (
                <motion.span key={index} variants={child}>
                    {letter}
                </motion.span>
            ))}
        </motion.span>
    );
}